import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
//thunk
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import {
  fetchSubCategoryById,
  updateSubCategory,
} from "slices/Masters/sub-category/thunk";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const SubCategorEditForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [initialData, setInitialData] = useState({
    category: {
      category_id: "",
      category_name: "",
    },
    subCategoryName: "",
  });

  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const res = await dispatch(fetchCategoryByDropDown()).unwrap();
        setCategoryOptions(res || []);
      } catch {
        toast.error("Failed to load categories");
      }
    };

    const fetchSubCategoryData = async () => {
      if (id) {
        try {
          setIsLoading(true);
          const data = await dispatch(fetchSubCategoryById(id)).unwrap();

          setInitialData({
            category: {
              category_id: data.category_id,
              category_name: data?.category?.category_name,
            },
            subCategoryName: data.sub_category_name,
          });
        } catch {
          toast.error("Failed to load sub-category");
          navigate("/sub-category");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDropdown();

    if (isEdit && id) {
      fetchSubCategoryData();
    }
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryId: initialData.category.category_id,
      subCategoryName: initialData.subCategoryName,
    },

    validationSchema: Yup.object({
      categoryId: Yup.string().required("Please select a category"),
      subCategoryName: Yup.string()
        .trim()
        .required("Please enter sub-category name"),
    }),

    onSubmit: async (values) => {
      const payload = {
        category_id: values.categoryId,
        sub_category_name: values.subCategoryName.trim(),
      };

      setIsSubmitting(true);
      try {
        await dispatch(updateSubCategory({ id, ...payload })).unwrap();
        toast.success("Sub-category updated successfully");
        navigate("/master/sub-category");
      } catch (error: any) {
        console.error("Error during form submit:", error);
        toast.error(
          error?.message || "Something went wrong while saving sub-category",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount, formik.isSubmitting);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Sub Category" : "Add Sub Category"}
        titleLink="/master/sub-category"
        breadcrumbs={[
          { label: "Sub Category", to: "/master/sub-category" },
          { label: id ? "Edit Sub Category" : "Add Sub Category", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6} className="mb-3">
              <Form.Label className="fw-semibold">
                Category <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={categoryOptions}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option.category_name
                }
                value={
                  categoryOptions.find(
                    (c: any) => c.category_id === formik.values.categoryId,
                  ) || null
                }
                onChange={(event, selected) =>
                  formik.setFieldValue(
                    "categoryId",
                    selected?.category_id || "",
                  )
                }
                onBlur={() => formik.setFieldTouched("categoryId", true)}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.category_name,
                  )
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Category"
                    name="categoryId"
                    error={
                      formik.touched.categoryId &&
                      Boolean(formik.errors.categoryId)
                    }
                    helperText={
                      formik.touched.categoryId && formik.errors.categoryId
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Col>

            <Col xs={12} md={6} className="mb-3">
              <Form.Label className="fw-semibold">
                Sub-category Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="subCategoryName"
                placeholder="Enter Sub Category name"
                value={formik.values.subCategoryName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.subCategoryName &&
                  formik.touched.subCategoryName
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.subCategoryName}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/sub-category"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default SubCategorEditForm;
